import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  clearUtmCatalogo,
  debeRegistrarIngreso,
  getOrCreateUtmSessionId,
  hasUtmActiva,
  hasUtmUrlParams,
  liberarMarcaIngreso,
  marcarIngresoRegistrado,
  markInicioRegistro,
  parseUtmFromSearch,
  readIdUtmCatalogo,
  readUtmStorage,
  UtmUrlParams,
  wasInicioRegistroMarked,
  writeUtmCatalogo,
} from '../core/utm-campaign.storage';
import { UserService } from './user.service';

/** Códigos alineados a NeoUtmEventoTipo (back). */
export const UtmEventoTipo = {
  INGRESO_CANAL: 'INGRESO_CANAL',
  INICIO_REGISTRO: 'INICIO_REGISTRO',
  REGISTRO_EXITOSO: 'REGISTRO_EXITOSO',
  SOLICITUD_CREADA: 'SOLICITUD_CREADA',
  SOLICITUD_ENVIADA_REVISION: 'SOLICITUD_ENVIADA_REVISION',
  OFERTA_ACEPTADA: 'OFERTA_ACEPTADA',
  FINALIZACION_SESION: 'FINALIZACION_SESION',
} as const;

export type UtmEventoTipoCode = (typeof UtmEventoTipo)[keyof typeof UtmEventoTipo];

export interface UtmEventoExtra {
  estadoProceso?: string;
  numeroDocumento?: string;
  razonSocial?: string;
  correoElectronico?: string;
  numeroSolicitud?: string;
  resultadoFlujo?: string;
}

/**
 * Captura UTM, storage mínimo (id catálogo + sesión) y registro de eventos.
 */
@Injectable({ providedIn: 'root' })
export class UtmTrackingService {
  private readonly base = `${environment.apiLocalUrl}utm`;
  /** Evita dos POST de INGRESO_CANAL por la misma campaña (ngOnInit + NavigationEnd). */
  private ingresoEnVuelo = new Set<number>();

  constructor(
    private readonly http: HttpClient,
    private readonly user: UserService
  ) {}

  /** Lee query de la URL, resuelve catálogo BX y persiste solo idUtmCatalogo. */
  capturarDesdeUrlActual(): void {
    try {
      const fromUrl = parseUtmFromSearch(window.location.search || '');
      if (fromUrl) {
        this.resolverCatalogo(fromUrl).subscribe((res) => {
          if (!res?.exitoso || !res.idUtm) {
            return;
          }
          const idCatalogo = Number(res.idUtm);
          writeUtmCatalogo(idCatalogo);
          if (!debeRegistrarIngreso(idCatalogo) || this.ingresoEnVuelo.has(idCatalogo)) {
            return;
          }
          // Marca inmediata (anti-carrera) + lock en memoria.
          this.ingresoEnVuelo.add(idCatalogo);
          marcarIngresoRegistrado(idCatalogo);
          this.registrar(UtmEventoTipo.INGRESO_CANAL).subscribe((reg) => {
            this.ingresoEnVuelo.delete(idCatalogo);
            const ok = reg != null && (reg as { exitoso?: boolean }).exitoso !== false;
            if (!ok) {
              liberarMarcaIngreso(idCatalogo);
            }
          });
        });
        return;
      }

      if (!hasUtmActiva(readUtmStorage())) {
        this.hidratarDesdeUltimoEvento().subscribe();
      }
    } catch {
      // no bloquear navegación
    }
  }

  sincronizarTrasIdentificar(
    extra?: {
      numeroDocumento?: string;
      correoElectronico?: string;
      razonSocial?: string;
    },
    _idUsuarioOverride?: number
  ): void {
    this.persistirDatosUsuarioEnSesion(extra);
    this.enriquecerEventosSesion(extra).subscribe();
    if (!readIdUtmCatalogo()) {
      this.hidratarDesdeUltimoEvento(extra).subscribe();
    }
  }

  registrarInicioRegistro(): void {
    if (wasInicioRegistroMarked()) {
      return;
    }
    markInicioRegistro();
    this.registrar(UtmEventoTipo.INICIO_REGISTRO).subscribe();
  }

  registrarRegistroExitoso(extra?: UtmEventoExtra, idUsuario?: number): void {
    this.registrar(UtmEventoTipo.REGISTRO_EXITOSO, extra).subscribe();
    this.sincronizarTrasIdentificar(extra, idUsuario);
  }

  registrarSolicitudCreada(numeroSolicitud?: string, extra?: UtmEventoExtra): void {
    this.registrar(UtmEventoTipo.SOLICITUD_CREADA, {
      ...extra,
      numeroSolicitud: numeroSolicitud || extra?.numeroSolicitud,
      estadoProceso: extra?.estadoProceso || 'CREADA',
    }).subscribe();
  }

  registrarSolicitudEnviada(numeroSolicitud?: string, extra?: UtmEventoExtra): void {
    this.registrar(UtmEventoTipo.SOLICITUD_ENVIADA_REVISION, {
      ...extra,
      numeroSolicitud: numeroSolicitud || extra?.numeroSolicitud,
      estadoProceso: extra?.estadoProceso || 'ENVIADA_REVISION',
    }).subscribe();
  }

  registrarOfertaAceptada(numeroSolicitud?: string, extra?: UtmEventoExtra): void {
    this.registrar(UtmEventoTipo.OFERTA_ACEPTADA, {
      ...extra,
      numeroSolicitud: numeroSolicitud || extra?.numeroSolicitud,
      // Tras aceptarPropuesta la solicitud queda en ESTADO_PENDIENTE_DESEMBOLSO (11)
      estadoProceso: extra?.estadoProceso || 'PENDIENTE_DESEMBOLSO',
      resultadoFlujo: extra?.resultadoFlujo || 'OFERTA_ACEPTADA',
    }).subscribe();
  }

  registrarFinalizacionSesion(): void {
    const user = this.safeUser();
    this.registrar(UtmEventoTipo.FINALIZACION_SESION, {
      resultadoFlujo: 'CIERRE_SESION',
      estadoProceso: 'SESION_CERRADA',
      numeroDocumento: this.pickDoc(user),
      correoElectronico: user?.correoElectronico,
      razonSocial: user?.razonSocial || user?.nombre,
    }).subscribe();
  }

  registrar(tipo: UtmEventoTipoCode, extra: UtmEventoExtra = {}): Observable<unknown> {
    const idUtm = readIdUtmCatalogo();
    if (!idUtm) {
      return of(null);
    }

    const user = this.safeUser();
    const body = {
      tipoEvento: tipo,
      idUtm,
      paginaIngreso: typeof window !== 'undefined' ? window.location.pathname : undefined,
      estadoProceso: extra.estadoProceso,
      numeroDocumento: extra.numeroDocumento || this.pickDoc(user),
      razonSocial: extra.razonSocial || user?.razonSocial || user?.nombre,
      correoElectronico: extra.correoElectronico || user?.correoElectronico,
      numeroSolicitud: extra.numeroSolicitud || (user?.idSolicitud ? String(user.idSolicitud) : undefined),
      resultadoFlujo: extra.resultadoFlujo,
      idSesionTracking: getOrCreateUtmSessionId(),
    };

    return this.http.post<{ exitoso?: boolean; codigoError?: string }>(`${this.base}/evento/registrar`, body).pipe(
      map((res) => {
        if (res && res.exitoso === false && res.codigoError === 'ERR_UTM_NO_CATALOGO') {
          clearUtmCatalogo();
        }
        return res;
      }),
      catchError(() => of(null))
    );
  }

  private resolverCatalogo(params: UtmUrlParams): Observable<{ exitoso?: boolean; idUtm?: number }> {
    let httpParams = new HttpParams();
    if (params.utmSource) {
      httpParams = httpParams.set('utmSource', params.utmSource);
    }
    if (params.utmMedium) {
      httpParams = httpParams.set('utmMedium', params.utmMedium);
    }
    if (params.utmCampaign) {
      httpParams = httpParams.set('utmCampaign', params.utmCampaign);
    }
    if (params.utmId) {
      httpParams = httpParams.set('utmId', params.utmId);
    }
    if (params.utmContent) {
      httpParams = httpParams.set('utmContent', params.utmContent);
    }
    return this.http
      .get<{ exitoso?: boolean; idUtm?: number }>(`${this.base}/resolver`, { params: httpParams })
      .pipe(catchError(() => of({ exitoso: false })));
  }

  private enriquecerEventosSesion(extra?: {
    numeroDocumento?: string;
    correoElectronico?: string;
    razonSocial?: string;
  }): Observable<unknown> {
    const user = this.safeUser();
    const numeroDocumento = extra?.numeroDocumento || this.pickDoc(user);
    const correoElectronico = extra?.correoElectronico || user?.correoElectronico;
    const razonSocial = extra?.razonSocial || user?.razonSocial || user?.nombre;
    if (!numeroDocumento && !correoElectronico && !razonSocial) {
      return of(null);
    }
    return this.http
      .post(`${this.base}/evento/enriquecer`, {
        idSesionTracking: getOrCreateUtmSessionId(),
        numeroDocumento,
        correoElectronico,
        razonSocial,
        estadoProceso: 'USUARIO_IDENTIFICADO',
      })
      .pipe(catchError(() => of(null)));
  }

  private persistirDatosUsuarioEnSesion(extra?: {
    numeroDocumento?: string;
    correoElectronico?: string;
    razonSocial?: string;
  }): void {
    try {
      const user = this.user.getUser();
      if (!user) {
        return;
      }
      let changed = false;
      if (extra?.numeroDocumento && !user.numeroDocumento) {
        user.numeroDocumento = String(extra.numeroDocumento).trim();
        changed = true;
      }
      if (extra?.correoElectronico && !user.correoElectronico) {
        user.correoElectronico = String(extra.correoElectronico).trim();
        changed = true;
      }
      if (extra?.razonSocial && !user.razonSocial) {
        user.razonSocial = String(extra.razonSocial).trim();
        changed = true;
      }
      if (changed) {
        this.user.saveUser(user);
      }
    } catch {
      // ignore
    }
  }

  private hidratarDesdeUltimoEvento(extra?: {
    numeroDocumento?: string;
    correoElectronico?: string;
  }): Observable<boolean> {
    const user = this.safeUser();
    const numeroDocumento = extra?.numeroDocumento || this.pickDoc(user);
    const correoElectronico = extra?.correoElectronico || user?.correoElectronico;
    if (!numeroDocumento && !correoElectronico) {
      return of(false);
    }
    let params = new HttpParams();
    if (numeroDocumento) {
      params = params.set('numeroDocumento', String(numeroDocumento));
    }
    if (correoElectronico) {
      params = params.set('correoElectronico', String(correoElectronico));
    }
    return this.http
      .get<{ exitoso?: boolean; activa?: { idUtmCatalogo?: number } | null }>(
        `${this.base}/evento/activa`,
        { params }
      )
      .pipe(
        map((res) => {
          const id = res?.activa?.idUtmCatalogo;
          if (res?.exitoso && id && id > 0) {
            writeUtmCatalogo(id);
            return true;
          }
          return false;
        }),
        catchError(() => of(false))
      );
  }

  private safeUser(): any {
    try {
      return this.user.getUser();
    } catch {
      return null;
    }
  }

  private pickDoc(user: any): string | undefined {
    if (!user) {
      return undefined;
    }
    return user.numeroDocumento || user.numDoc || user.documento || undefined;
  }
}
